/**
 * Recently Viewed Products — Granada Maison
 * Saves current product to localStorage on product pages,
 * and renders a product grid in the <recently-viewed-products> custom element.
 */

(function () {
  const STORAGE_KEY = 'gm_recently_viewed';
  const MAX_STORED = 20;

  // ── Save current product ─────────────────────────────────────────────────
  function saveCurrentProduct() {
    const dataEl = document.getElementById('recently-viewed-product-data');
    if (!dataEl) return;

    try {
      const product = JSON.parse(dataEl.textContent);
      if (!product || !product.handle) return;

      let viewed = getStoredProducts();
      viewed = viewed.filter((p) => p.handle !== product.handle);
      viewed.unshift(product);
      viewed = viewed.slice(0, MAX_STORED);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(viewed));
    } catch (e) {
      // silently ignore
    }
  }

  function getStoredProducts() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  // Save as soon as the script loads (product page context)
  saveCurrentProduct();

  // ── Custom element ───────────────────────────────────────────────────────
  if (customElements.get('recently-viewed-products')) return;

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      this.renderProducts();
    }

    renderProducts() {
      const grid = this.querySelector('#recently-viewed-grid');
      const container = this.querySelector('#recently-viewed-container');
      if (!grid || !container) return;

      const maxToShow    = parseInt(this.dataset.productsToShow, 10) || 5;
      const cardStyle    = this.dataset.cardStyle || 'standard';
      const cardScheme   = this.dataset.cardColorScheme || 'scheme-1';
      const moneyFormat  = this.dataset.moneyFormat || '€{{amount}}';
      const imageRatio   = this.dataset.imageRatio || 'adapt';
      const imageShape   = this.dataset.imageShape || 'default';
      const showVendor   = this.dataset.showVendor === 'true';

      let currentHandle = null;
      try {
        currentHandle = JSON.parse(this.dataset.currentProduct || 'null');
      } catch (e) {}

      let products = getStoredProducts();

      // Exclude the product currently being viewed
      if (currentHandle) {
        products = products.filter((p) => p.handle !== currentHandle);
      }

      products = products.slice(0, maxToShow);

      if (products.length === 0) return;

      grid.innerHTML = products
        .map((p) => this.buildCard(p, cardStyle, cardScheme, moneyFormat, imageRatio, imageShape, showVendor))
        .join('');

      container.style.display = '';
    }

    // ── Money formatter ────────────────────────────────────────────────────
    // Handles all Shopify money format tokens, with or without spaces inside {{ }}
    formatMoney(cents, format) {
      if (!cents) cents = 0;
      const rounded = String(Math.round(cents / 100));
      const decimal = (cents / 100).toFixed(2).replace('.', ',');

      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
        switch (token.trim()) {
          case 'amount':                              return decimal;
          case 'amount_with_comma_separator':         return decimal;
          case 'amount_no_decimals':                  return rounded;
          case 'amount_no_decimals_with_comma_separator': return rounded;
          default:                                    return rounded;
        }
      });
    }

    // ── Price block ────────────────────────────────────────────────────────
    buildPrice(product, moneyFormat) {
      const price = this.formatMoney(product.price, moneyFormat);
      const hasCompare =
        product.compareAtPrice && product.compareAtPrice > product.price;

      if (!product.available) {
        return `<div class="price price--sold-out">
          <div class="price__container">
            <span class="visually-hidden">Precio</span>
            <span class="price-item price-item--regular">${price}</span>
          </div>
        </div>`;
      }

      if (hasCompare) {
        const comparePrice = this.formatMoney(product.compareAtPrice, moneyFormat);
        return `<div class="price price--on-sale">
          <div class="price__container">
            <span class="visually-hidden">Precio de oferta</span>
            <span class="price-item price-item--sale price-item--last">${price}</span>
            <span class="visually-hidden">Precio habitual</span>
            <s class="price-item price-item--regular">${comparePrice}</s>
          </div>
        </div>`;
      }

      return `<div class="price">
        <div class="price__container">
          <span class="visually-hidden">Precio habitual</span>
          <span class="price-item price-item--regular">${price}</span>
        </div>
      </div>`;
    }

    // ── Card HTML ──────────────────────────────────────────────────────────
    buildCard(product, cardStyle, cardColorScheme, moneyFormat, imageRatio, imageShape, showVendor) {
      const hasImage = !!product.image;

      // Calculate aspect ratio the same way card-product.liquid does
      let ratio = 1;
      if (hasImage) {
        if (imageRatio === 'portrait') {
          ratio = 0.8;
        } else if (imageRatio === 'adapt') {
          ratio = parseFloat(product.imageAspectRatio) || 1;
          if (ratio === 0) ratio = 1;
        }
      }
      const ratioPercent = (1 / ratio * 100).toFixed(4);

      const hasDimensions = product.ancho || product.alto;
      const dimensionsHtml = hasDimensions
        ? `<div class="caption-with-letter-spacing light">${this.esc(product.ancho) || ''} x ${this.esc(product.fondo) || ''} x ${this.esc(product.alto) || ''}</div>`
        : '';

      const vendorHtml = showVendor && product.vendor
        ? `<div class="caption-with-letter-spacing light">${this.esc(product.vendor)}</div>`
        : '';

      const priceHtml = this.buildPrice(product, moneyFormat);

      // Image shape classes
      const shapeClass = imageShape && imageShape !== 'default' ? ` shape--${imageShape} color-${cardColorScheme} gradient` : '';
      const cardShapeClass = imageShape && imageShape !== 'default' ? ' card--shape' : '';

      const imageHtml = hasImage
        ? `<div class="card__media${shapeClass}">
            <div class="media media--transparent media--hover-effect">
              <img
                src="${product.image}"
                alt="${this.esc(product.title)}"
                class="motion-reduce"
                loading="lazy"
                width="533"
                height="533"
              >
            </div>
          </div>`
        : '';

      const soldOutBadge = !product.available
        ? `<div class="card__badge top-left">
            <span class="badge badge--bottom-left color-inverse">Agotado</span>
          </div>`
        : '';

      const cardMediaClass  = hasImage ? 'card--media' : 'card--text';
      const cardExtraClass  = cardStyle === 'card'     ? ` color-${cardColorScheme} gradient` : '';
      const innerExtraClass = cardStyle === 'standard' ? ` color-${cardColorScheme} gradient` : '';
      const innerRatioClass = hasImage || cardStyle === 'standard' ? ' ratio' : '';

      return `<li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div
            class="card card--${cardStyle} ${cardMediaClass}${cardShapeClass}${cardExtraClass}"
            style="--ratio-percent: ${ratioPercent}%;"
          >
            <div
              class="card__inner${innerExtraClass}${innerRatioClass}"
              style="--ratio-percent: ${ratioPercent}%;"
            >
              ${imageHtml}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${product.url}" class="full-unstyled-link">
                      ${this.esc(product.title)}
                    </a>
                  </h3>
                </div>
                ${soldOutBadge}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${this.esc(product.title)}
                  </a>
                </h3>
                <div class="card-information">
                  ${vendorHtml}
                  ${dimensionsHtml}
                  ${priceHtml}
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>`;
    }

    // ── HTML escape helper ─────────────────────────────────────────────────
    esc(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();